import React, { useState } from 'react';
import { useApp } from './context/AppContext';
import { useLanguage } from './context/LanguageContext';
import { Header } from './components/Header';
import { CustomerWelcomeModal } from './components/CustomerWelcomeModal';
import { MenuSection } from './components/MenuSection';
import { CartDrawer } from './components/CartDrawer';
import { LiveOrderTracker } from './components/LiveOrderTracker';
import { StaffDashboard } from './components/StaffDashboard';
import { StaffSidebar } from './components/StaffSidebar';
import { MenuManagement } from './components/MenuManagement';
import { QRStudio } from './components/QRStudio';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { HeroBannerStudio } from './components/HeroBannerStudio';
import { FooterSettingsStudio } from './components/FooterSettingsStudio';
import { StaffLoginModal } from './components/StaffLoginModal';
import { StaffUserManagement } from './components/StaffUserManagement';
import { CustomerManagementStudio } from './components/CustomerManagementStudio';
import { BookingsManagementStudio } from './components/BookingsManagementStudio';
import { CustomerOrderHistoryModal } from './components/CustomerOrderHistoryModal';
import { CustomerOrderHistoryPage } from './components/CustomerOrderHistoryPage';
import { StaffAuthGate } from './components/StaffAuthGate';
import { LegalModal, LegalModalType } from './components/LegalModal';
import { LandingPage } from './components/LandingPage/LandingPage';
import {
  Flame,
  ShieldCheck,
  Lock,
  ChefHat,
  UserCheck,
  Shield,
  Clock,
  FileText,
  AlertCircle,
  History,
  QrCode
} from 'lucide-react';

export const App: React.FC = () => {
  const {
    currentView,
    setCurrentView,
    currentTable,
    activeCustomerOrder,
    restaurantProfile,
    currentStaffRole,
    activeStaffUser,
    isStaffLoginOpen,
    setIsStaffLoginOpen,
    openStaffLogin,
    isOrderHistoryOpen,
    setIsOrderHistoryOpen,
    openOrderHistory
  } = useApp();
  const { lang, t } = useLanguage();

  const [activeLegalModal, setActiveLegalModal] = useState<LegalModalType>(null);

  // 1. PRIMARY HOMEPAGE / LANDING VIEW
  if (currentView === 'landing') {
    return (
      <>
        <LandingPage
          onOpenOrderApp={() => {
            window.history.pushState(null, '', '/order');
            setCurrentView('customer');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onOpenStaffPortal={() => openStaffLogin('admin')}
          onOpenLegalModal={(type) => setActiveLegalModal(type)}
        />

        {/* Customer Legal Policy Modal (GDPR / Terms / Allergens) */}
        <LegalModal
          type={activeLegalModal}
          onClose={() => setActiveLegalModal(null)}
        />

        {/* Staff Role Login Modal */}
        <StaffLoginModal
          isOpen={isStaffLoginOpen}
          onClose={() => setIsStaffLoginOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="app-container">
      {/* Universal Top Header */}
      <Header />

      {/* Main Content Area */}
      <main className="main-content">
        {/* 1. CUSTOMER MODE (Dedicated QR Table Ordering) */}
        {currentView === 'customer' && (
          <>
            {/* Dedicated QR Table Banner */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
                background: 'linear-gradient(135deg, rgba(224, 90, 54, 0.12) 0%, rgba(245, 158, 11, 0.08) 100%)',
                border: '1px solid rgba(224, 90, 54, 0.25)',
                borderRadius: '16px',
                padding: '14px 20px',
                marginBottom: '20px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'var(--accent-copper)',
                    color: '#ffffff',
                    padding: '6px 14px',
                    borderRadius: '999px',
                    fontWeight: 800,
                    fontSize: '0.92rem',
                    letterSpacing: '0.02em'
                  }}
                >
                  <QrCode size={16} />
                  <span>{t('table')} {currentTable?.table_number || '1'}</span>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                    {lang === 'fi' ? 'Pöytätilaus aktiivinen' : lang === 'sv' ? 'Bordsbeställning aktiv' : 'Table Ordering Active'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {lang === 'fi'
                      ? 'Tilauksesi valmistetaan ja toimitetaan suoraan tähän pöytään'
                      : lang === 'sv'
                      ? 'Din beställning tillagas och levereras direkt till detta bord'
                      : 'Your order is prepared and served directly to this table'}
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '0.82rem', color: 'var(--accent-amber)', fontWeight: 600 }}>
                {restaurantProfile.name} · Aleksanterinkatu 18
              </div>
            </div>

            {/* If Customer has an active live order, display tracker */}
            {activeCustomerOrder && (
              <div style={{ marginBottom: '32px' }}>
                <LiveOrderTracker />
              </div>
            )}

            {/* Menu Explorer with dynamic Hero & Offer Banners */}
            <MenuSection />

            {/* Slide-over Cart & Floating Checkout Button */}
            <CartDrawer />

            {/* Identification & GDPR Consent Modal */}
            <CustomerWelcomeModal />
          </>
        )}

        {/* 1.1 DEDICATED FULL-SCREEN ORDER HISTORY PAGE */}
        {currentView === 'order-history' && (
          <>
            <CustomerOrderHistoryPage />
            <CartDrawer />
          </>
        )}

        {/* 2. STAFF & MANAGEMENT MODES (Strictly Authenticated & Role-Gated) */}
        {currentView.startsWith('staff-') && (
          <div>
            {(!currentStaffRole || !activeStaffUser) ? (
              <StaffAuthGate />
            ) : (
              <div className="staff-layout-wrapper">
                <StaffSidebar />
                <div className="staff-main-content">
                  {currentView.startsWith('staff-live') && <StaffDashboard />}
                  {currentView.startsWith('staff-menu') && currentStaffRole !== 'kitchen' && <MenuManagement />}
                  {currentView.startsWith('staff-banners') && currentStaffRole === 'admin' && <HeroBannerStudio />}
                  {currentView.startsWith('staff-qr') && currentStaffRole !== 'kitchen' && <QRStudio />}
                  {currentView.startsWith('staff-analytics') && currentStaffRole !== 'kitchen' && <AnalyticsDashboard />}
                  {currentView.startsWith('staff-settings') && currentStaffRole === 'admin' && <FooterSettingsStudio />}
                  {currentView.startsWith('staff-users') && currentStaffRole === 'admin' && <StaffUserManagement />}
                  {currentView.startsWith('staff-customers') && currentStaffRole !== 'kitchen' && <CustomerManagementStudio />}
                  {currentView.startsWith('staff-bookings') && currentStaffRole !== 'kitchen' && <BookingsManagementStudio />}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Customer Order History Modal */}
      <CustomerOrderHistoryModal
        isOpen={isOrderHistoryOpen}
        onClose={() => setIsOrderHistoryOpen(false)}
      />

      {/* Staff Role Login Modal */}
      <StaffLoginModal
        isOpen={isStaffLoginOpen}
        onClose={() => setIsStaffLoginOpen(false)}
      />

      {/* Customer Legal Policy Modal (GDPR / Terms / Allergens) */}
      <LegalModal
        type={activeLegalModal}
        onClose={() => setActiveLegalModal(null)}
      />

      {/* Dynamic Footer (Fully Editable by Admin, Finland Compliant) */}
      <footer
        className="no-print"
        style={{
          background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border-subtle)',
          padding: '36px 20px 24px',
          marginTop: 'auto',
          color: 'var(--text-muted)',
          fontSize: '0.85rem'
        }}
      >
        <div
          style={{
            maxWidth: 'var(--max-content-width)',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          {/* Main Footer Info Row */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '24px'
            }}
          >
            {/* Restaurant Info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                <Flame size={18} color="var(--accent-copper)" />
                <span>{restaurantProfile.name}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--accent-copper)', background: 'var(--accent-copper-light)', padding: '2px 8px', borderRadius: 'var(--radius-full)' }}>
                  {restaurantProfile.domain}
                </span>
              </div>
              <div style={{ color: 'var(--text-secondary)' }}>
                {restaurantProfile.address}, {restaurantProfile.postal_code} {restaurantProfile.city}, Finland
              </div>
              <div style={{ fontSize: '0.8rem' }}>
                Y-tunnus: {restaurantProfile.business_id} • Puh: {restaurantProfile.phone}
                {restaurantProfile.email && ` • Email: ${restaurantProfile.email}`}
              </div>
            </div>

            {/* Compliance Badge & Discrete Staff Portal Button */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: 'var(--accent-emerald)', fontWeight: 600 }}>
                <ShieldCheck size={16} />
                <span>
                  {lang === 'en'
                    ? (restaurantProfile.compliance_text_en || restaurantProfile.compliance_text_fi)
                    : restaurantProfile.compliance_text_fi}
                </span>
              </div>

              {/* Discrete Staff Portal Access Button */}
              <button
                onClick={() => openStaffLogin('kitchen')}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-subtle)',
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <Lock size={13} color="var(--accent-copper)" />
                <span>Staff Portal</span>
              </button>
            </div>
          </div>

          {/* Legal Pages & History Links */}
          <div
            style={{
              borderTop: '1px solid var(--border-subtle)',
              paddingTop: '14px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              fontSize: '0.8rem'
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px' }}>
              {/* Customer Past Orders History Button */}
              <button
                onClick={openOrderHistory}
                style={{
                  color: 'var(--accent-copper)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 700,
                  background: 'rgba(224, 90, 54, 0.08)',
                  border: '1px solid rgba(224, 90, 54, 0.25)',
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-full)',
                  cursor: 'pointer'
                }}
              >
                <History size={14} />
                <span>{lang === 'en' ? 'My Order History' : 'Oma tilaushistoria'}</span>
              </button>

              <span style={{ color: 'var(--border-medium)' }}>•</span>

              <button
                onClick={() => setActiveLegalModal('privacy')}
                style={{
                  color: 'var(--accent-copper)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600
                }}
              >
                <ShieldCheck size={15} />
                <span>{lang === 'en' ? 'Privacy Policy (GDPR)' : 'Tietosuojaseloste (GDPR)'}</span>
              </button>

              <span style={{ color: 'var(--border-medium)' }}>•</span>

              <button
                onClick={() => setActiveLegalModal('terms')}
                style={{
                  color: 'var(--accent-copper)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600
                }}
              >
                <FileText size={15} />
                <span>{lang === 'en' ? 'Terms of Ordering' : 'Tilaus- ja Käyttöehdot'}</span>
              </button>

              <span style={{ color: 'var(--border-medium)' }}>•</span>

              <button
                onClick={() => setActiveLegalModal('allergen')}
                style={{
                  color: 'var(--accent-copper)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600
                }}
              >
                <AlertCircle size={15} />
                <span>{lang === 'en' ? 'Allergens & Food Safety (Oiva)' : 'Allergeenit & Oiva-valvonta'}</span>
              </button>
            </div>

            {/* Opening Hours */}
            {(restaurantProfile.opening_hours_en || restaurantProfile.opening_hours_fi) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.78rem',
                  color: 'var(--text-muted)'
                }}
              >
                <Clock size={14} color="var(--accent-amber)" />
                <span>
                  <strong>{lang === 'en' ? 'Hours:' : 'Aukioloajat:'}</strong>{' '}
                  {lang === 'en' ? (restaurantProfile.opening_hours_en || restaurantProfile.opening_hours_fi) : restaurantProfile.opening_hours_fi}
                </span>
              </div>
            )}
          </div>

          {/* Copyright & Disclaimer */}
          <div
            style={{
              borderTop: '1px solid var(--border-subtle)',
              paddingTop: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px',
              fontSize: '0.75rem',
              color: 'var(--text-dim)'
            }}
          >
            <div>
              {restaurantProfile.copyright_text || `© 2026 ${restaurantProfile.name}. All rights reserved.`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span>{restaurantProfile.domain}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
export default App;
